<template>
	<b-container fluid class="p-0 no-gutters h-100">
		<b-row class="h-100 p-0 m-0">
			<b-col align-self="center" class="p-0">
				<b-breadcrumb class="m-0 align-items-center py-0 pl-3 bg-transparent">
					<!-- Accueil quand il n'y a pas de regroupement -->
					<b-breadcrumb-item class="h-100 align-middle"><!-- v-else -->
						<b-button id="groupingName" :pressed.sync="inGrouping" :variant="(inGrouping ? '' : 'outline-') + Variant">
							<template v-if="inGrouping || (Grouping && Object.keys(Grouping).length > 0)">
								<b-iconstack class="mr-1">
									<b-icon stacked icon="house-fill"/><!-- house-fill -->
									<b-icon stacked icon="circle-fill" scale="0.5" shift-v="-4" shift-h="4" :class="'text-' + Variant" />
									<b-icon stacked icon="info-circle-fill" scale="0.5" shift-v="-4" shift-h="4" />
								</b-iconstack><u>{{ (Grouping && Object.keys(Grouping).length > 0) ? Grouping.Name :  $t('Grouping') }}</u>
								<b-tooltip target="groupingName" noninteractive>
									{{ $t('Grouping') }} : <b>{{ Grouping.Name }}</b>
								</b-tooltip>
							</template>
							<template v-else>
								<b-iconstack class="mr-1">
									<b-icon stacked icon="house"/>
									<b-icon stacked icon="circle-fill" variant="white" scale="0.5" shift-v="-4" shift-h="4" />
								</b-iconstack><u>{{ $t('Heritage') }}</u>
							</template>
						</b-button>
					</b-breadcrumb-item>
					
					<!-- Chemin du noeud sélectionné (SingleNode) (⚠ sauf s'il s'agit du noeud client) -->
					<template v-if="!inGrouping && selectMode===selectModeEnum.Single && singleNode && Object.keys(singleNode).length > 0 && singleNode.ObjectType !== baseObjectTypeEnum.Client && singleNode.Id">
						<!-- Chemin des ascendants de l'item en cours (SingleNode ou équipement) -->
						<b-breadcrumb-item v-for="node in nodes(singleNode)" :key="node.Id" @click="selectNode(node)" class="h-100">
							<b-img :src="AbsoluteLink(node.Icon)" height="20rem" width="20rem" class="mr-1 m-0" /><u class="align-middle">{{ node.Name }}</u>
						</b-breadcrumb-item>

						<!-- Item s'il s'agit d'un LO, LBO, BO ou équipement -->
						<b-breadcrumb-item @click="setLocalSingleNode(singleNode)" :active="saveMode!==saveModeEnum.NewObject" :class="['h-100', { [`text-${Variant}`]: saveMode!==saveModeEnum.NewObject }]" role="button">
							<b-img :src="AbsoluteLink(singleNode.Icon)" height="20rem" width="20rem" class="mr-1" /><u class="align-middle">{{ singleNode.Name }}</u>
						</b-breadcrumb-item>
						
						<!-- Bouton d'accès à la map -->
						<b-button v-if="saveMode!==saveModeEnum.NewObject" @click.stop.prevent="goToTheMap(singleNode)" v-b-tooltip.hover :title="$t('ShowOnMap')" :variant="'outline-' + Variant" class="btn-circle-small ml-2 p-0 d-flex align-items-center justify-content-center"> <!-- Afficher sur la carte -->
							<b-icon icon="geo-alt" font-scale="0.95" />
						</b-button>

						<!-- Item fictif s'il s'agit d'un nouvel objet en cours de création -->
						<div v-if="saveMode==saveModeEnum.NewObject" :class="'d-flex flex-column flex-grow-1 font-weight-bold font-italic text-right pr-4 text-' + Variant">
							{{ $t('NewObjectBeingCreated') }} <!-- Nouvel objet en cours de création -->
						</div>
					</template>
					<template v-else>
						<b-col cols="auto" class="p-0">
							<portal-target  name="NavbarPortal" class="mx-1" />
						</b-col>
					</template>
				</b-breadcrumb>
			</b-col>
		</b-row>
	</b-container>
</template>

<script lang="ts">
	import Component, { mixins } from 'vue-class-component';
	import { Getter } from 'vuex-class';
	import { MAIN_FILTER_TREE } from '@/App.settings';
	import SaveModeEnum from '@/enums/SaveModeEnum';
	import TreeNode from '@/types/TreeNode';
	import { LinkUtils, ObjectUtils } from "@/components/commons/Utils";
	import WorkingModeEnum from '@/enums/WorkingModeEnum';
	import SelectModeEnum from '@/enums/SelectModeEnum';
	import BaseObjectTypeEnum from '@/enums/BaseObjectTypeEnum';
	import { Inject, InjectReactive, Prop, PropSync } from 'vue-property-decorator';
	import FilterT from '@/types/FilterTree/FilterT';
	import locales from './locales/Navbar.json';
	/** Médiateur de communication inter-composants/stores VueX dédié à la navigation */
	import { NavigationMediator } from '@/mediators/NavigationMediator';
	import INodeSelector from '@/interfaces/INodeSelector';

	/** Composant affichant le chemin du noeud sélectionné dans l'arborescence ou bien de l'équipement ouvert dans la fiche objet (non présent dans l'arborescence) */
	@Component({
		components: {},
		i18n : { messages : locales }
	})
	export default class Navbar extends mixins(LinkUtils, ObjectUtils) implements INodeSelector {
		//#region Pour pouvoir utiliser les enums dans la section template
		private saveModeEnum = SaveModeEnum;
		private baseObjectTypeEnum = BaseObjectTypeEnum;
		private selectModeEnum = SelectModeEnum;
		//#endregion Pour pouvoir utiliser les enums dans la section template

		/** Variant de couleur à appliquer */
        @InjectReactive()
        private Variant!: string;

		/** Indique si une gestion de regroupement est active ou non
		 * @todo A gérer dans le GlobalStore
		 */
		@Prop()
		public InGrouping!: boolean;

		/** Mode de sélection de la fonction en cours */
        @Getter(MAIN_FILTER_TREE + '/GetSelectMode')
        private selectMode!: SelectModeEnum;

		/** Indique le mode de sauvegarde en cours dans la fiche objet */
		@Getter('ObjectStore/GetSaveMode')
		private saveMode!: SaveModeEnum;

		/** Obtient les noeuds parents d'un noeud */
		@Getter(MAIN_FILTER_TREE + '/GetNodePath')
		private nodes!: (node: TreeNode) => TreeNode[];

        //#region Regroupements
        /** Indique si le bouton de regroupement est affiché ou non */
        @Prop()
        public ManageGrouping!:boolean;

        /** Indique la fenêtre de gestion des regroupements est affichée ou non */
        @PropSync('InGrouping')
        private inGrouping!: boolean;

		/** Indique le regroupement en cours */
		@InjectReactive()
		public Grouping!: FilterT;

        //#endregion Regroupements

        /** Clé utilisée pour forcer le rafraichissement de la balise <component> pour rechargement de la fonction en cours d'utilisation */
		private lastNodeClickTime: Date = undefined;

		private setLocalSingleNode(singleNode: TreeNode) {
			let newNodeClickTime: Date = new Date();

			if (!this.lastNodeClickTime || ((newNodeClickTime.getTime() - this.lastNodeClickTime.getTime()) >= 2000))
				this.selectNode(singleNode);
            else
                console.warn("Navbar - updateSingleNode() - Clic trop rapproché de 2 secondes. Il n'a pas été pris en compte !");

            this.lastNodeClickTime = newNodeClickTime;
		}

		/** Ouvre la map et affiche l'objet sur celle-ci */
		private async goToTheMap(node: TreeNode) {
			/** @todo Crado. Voir pour implémenter une state machine pour gérer cela */
			await this.$store.dispatch('ToggleMode', WorkingModeEnum.Map);

			// On affiche ou masque la map (qui est sur Workbench.vue au-dessus de Navbar.vue)
			await this.$store.dispatch("CartoMod/SetIsVisible", true);
			
			// On attend que la carte s'affiche pour qu'elle puisse réagir
			this.$nextTick(() => {
				this.$nextTick(() => {
					this.$store.dispatch("CartoMod/SelectNode", { nodeId: node.Id, objectType:node.ObjectType });
				});
			});
		}

		private selectNode(node: TreeNode) {
			this.$store.dispatch(MAIN_FILTER_TREE +  "/SelectNode", { node: node, openNode: true, scrollIntoView: false });
		}
		
        @Inject()
        private navigationMediator: NavigationMediator;

        private singleNode: TreeNode = {} as TreeNode;

        public SetNode(node: TreeNode): Promise<any> {
            return new Promise(async (resolve, reject) => {
                this.singleNode = node;
                resolve('SingleNode affecté !');
            });
        }

        mounted() {
            this.navigationMediator.Register(this);
        }

        beforeDestroy() {
            this.navigationMediator.Unregister(this);
        }
	}
</script>

<style scoped>
	/** @todo Voir pour plutôt utiliser Link / Regular Text link dans les variables Bootstrap (default-theme.scss) */
	:deep(.breadcrumb-item > a) {
		color: var(--black);
		text-decoration: none !important;
	}
</style>